import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useWeddingDayStore } from './store';
import {
  bootstrap, createActivity, createCropSlot, deletePhoto,
  fetchStatus, finishCrop,
  patchActivity, patchContact, patchCoupleShot, patchEventName, patchEventOrder,
  patchEventConfirmed, patchEventSide, patchInterview,
  patchInterviewChoice,
  patchEventTiming, patchFace, patchSection, postPhotoFaces, submitWeddingDay,
  uploadToS3,
} from './api';
import { cropFace, findFaces, readImage, releaseImage }
  from '../faces';
import { emptyDoc, groupOf } from './emptyDoc';
import { goToLogin } from '../runtime';
import { getBriefIds, isReadOnly, setPayloadReadOnly, setSession } from './session';
import {
  BRIEF_PATTERN, DETECTION, FIELD_DEBOUNCE_MS, HANDLED_REJECTS, KIND, NEEDS_PHOTO, NO_FACES,
  REJECT, RETRY_DELAYS, SHARED_POLL_MS, SURPRISE, TEXT_DEBOUNCE_MS, TIMING,
  WRITE_LABELS,
} from '../../constants';
import { entryById, resolveDate, timelineStopKeys } from './derive';

// Module-scoped so the timers survive re-renders without a context. Internal
// to the retry logic; nothing outside this module reads them.
// Each entry is {timer, run}: a section save, a contact and an event's venue
// all wait here, so the waiting write has to carry its own way of going out
// rather than leaving the flush to guess at one.
const pendingText = {};

/** Hold a write back until the typing stops, replacing any earlier wait. */
const holdText = (key, run, wait) => {
  if (pendingText[key]) clearTimeout(pendingText[key].timer);
  pendingText[key] = {
    run,
    timer: setTimeout(() => {
      delete pendingText[key];
      run();
    }, wait),
  };
};
/**
 * Send one held write now, and say when it has landed.
 *
 * A write that has to follow it waits on this. Nothing held is resolved
 * immediately, so the caller can wait either way.
 */
const flushHeld = (key) => {
  const held = pendingText[key];
  if (!held) return Promise.resolve();
  clearTimeout(held.timer);
  delete pendingText[key];
  return Promise.resolve(held.run());
};
// The last write still on its way, by key. A write that only makes sense
// once an earlier one has landed waits on this instead of racing it.
const inFlight = {};

/** Everything owed on this key sent and landed, held back or already away. */
const settled = (key) =>
  flushHeld(key).then(() => inFlight[key] || Promise.resolve());
const retryTimers = {};
// Partial writes the server has not taken yet, merged by key. A newer write
// on a key cancels the older one's retry, so whatever the older one carried
// has to travel with the newer, or a field set a moment earlier never lands.
const owedPatch = {};
// Each key's own save state. One value for the whole form let a note that
// landed clear the warning a lost timing had left, so the banner shows the
// worst of them, and a key only goes quiet when its own write lands.
const keyStates = {};
const STATE_RANK = { failed: 4, rejected: 3, error: 2, saving: 1 };
// The writes that ran out of retries, each able to send itself again. Kept
// so submit can try them once more rather than refuse for good, and gone
// the moment a write on the same key lands, deleting the item included.
const failedWrites = {};
const writeLabel = (key) =>
  (WRITE_LABELS.find(([prefix]) => key.indexOf(prefix) === 0) || [])[1]
  || 'an answer';
const worstState = () => Object.keys(keyStates).reduce(
  (worst, k) => ((STATE_RANK[keyStates[k]] || 0) > (STATE_RANK[worst] || 0)
    ? keyStates[k] : worst),
  'idle'
);
const owe = (key, patch) => {
  owedPatch[key] = { ...(owedPatch[key] || {}), ...patch };
};
// The photographs this session has detected on, by the row they were filed
// against: the file itself and the object URL the cards draw. They are only
// ever held here. Nothing uploads them, and closing the page is what
// discards them.
//
// The file, not the decoded pixels. A photograph off a phone is tens of
// megabytes once decoded, and keeping one per photograph made adding the
// next one slow. A crop decodes it again for the moment it needs it.
const pictures = {};

// Counts the family panels' local writes. Picking a file blurs the window
// and closing the picker refocuses it, which starts a refresh alongside the
// upload. Once detection is warm the upload answers first, so the refresh
// would land afterwards carrying the panels as they stood before the
// photograph and take it back off screen.
//
// Module level, like the pictures themselves: every component calls this
// hook, so a ref would give each of them a count of its own and the one the
// refresh reads would never move.
const familyStamp = { count: 0 };

// Counts every write this session has started. A read of the server carries
// the count it began with: if any write has gone out since, the answer in
// its hands was made before that write and is no longer what the form says.
//
// Module level for the same reason the family's count is: every component
// calls this hook, so a ref would give each of them a count of its own.
const writeStamp = { count: 0 };

// Cards whose typed map link the server refused. The link stays on screen
// for the couple to finish, so the time and venue writes leave it out until
// a link write goes through: sent with them, it would refuse those as well.
const refusedLinks = {};

/** One panel's photos, each given back the picture this session holds. */
const withPictures = (panel) => ({
  ...panel,
  photos: (panel.photos || []).map((photo) => ({
    ...photo,
    photo_url: (pictures[String(photo.photo_id)] || {}).url || '',
  })),
});

/**
 * Every side's panel, with the pictures this session is holding put back.
 *
 * The server cannot send them: it never had them. So every read of the
 * family goes through here, or a refresh would drop the photographs the
 * couple is still looking at and the cards would say they had gone.
 */
const withSessionPictures = (family) => {
  const next = { ...(family || {}) };
  Object.keys(next).forEach((side) => {
    next[side] = withPictures(next[side]);
  });
  return next;
};

/**
 * Send the page to the app this reader's form lives in, and say whether it
 * went. Same origin means they are already there and nothing moves. A root
 * the deployment has not set leaves them where they are rather than throwing.
 * Only a web address is followed: the url comes off a response, and a
 * javascript: one would run in this page rather than move it.
 */
const movedHome = (url) => {
  let target;
  try {
    target = new URL(url);
  } catch (err) {
    return false;
  }
  if (target.protocol !== 'http:' && target.protocol !== 'https:') return false;
  if (target.origin === window.location.origin) return false;
  window.location.replace(target.href);
  return true;
};

/**
 * Reads the form out of the store and returns the actions that write to it.
 * The structure comes from `wdSections` and the event entries from `wdEvents`,
 * both straight from the server. Two people share the form, so a response is
 * always trusted over the local copy it answers.
 */
export const useWeddingDay = ({ bookingId, briefUuid, readOnly, asRole } = {}) => {
  const {
    wdDoc, wdSections, wdEvents, wdVenues, wdWindows, wdOpens, wdSpan,
    wdLocksAt,
    wdClosed, wdWantsInterview,
    wdAskInterview,
    wdInterviewQuestion, wdLimits, wdFamily, wdBookingName,
    wdLoading, wdSaveState, wdSection, wdPhase, wdActiveDate,
    wdDatePage, wdCanEdit, wdDenied, wdDeniedCode, wdFieldErrors, wdToast,
    wdNeedsInterview, wdInterviews, wdRitualOptions, wdContacts,
    wdRole, wdSharedLocked, wdChangeLog,
    wdDeniedMessage, wdInvalidLink, wdLoadFailed,
    setWdDoc, setWdSections, setWdEvents, setWdVenues, setWdWindows,
    setWdOpens, setWdSpan,
    setWdLocksAt, setWdClosed,
    setWdWantsInterview, setWdAskInterview, setWdInterviewQuestion, setWdLimits,
    setWdFamily, setWdBookingName, setWdLoading, setWdSaveState,
    setWdSection, setWdPhase, setWdActiveDate, setWdDatePage,
    setWdCanEdit, setWdDenied, setWdDeniedCode, setWdDeniedMessage, setWdInvalidLink,
    setWdLoadFailed, setWdFieldErrors,
    setWdToast, setWdNeedsInterview, setWdInterviews, setWdRitualOptions,
    setWdContacts,
    setWdRole, setWdSharedLocked,
    setWdChangeLog,
  } = useWeddingDayStore();

  // Set before anything below reads it, so the route component's own call sees
  // the same values every other call site will.
  if (bookingId !== undefined) setSession({ bookingId, briefUuid, readOnly, asRole });

  const doc = wdDoc || emptyDoc();
  // Memoised so the fallbacks do not make a new array every render.
  const sections = useMemo(() => wdSections || [], [wdSections]);
  const events = useMemo(() => wdEvents || [], [wdEvents]);
  const venues = useMemo(() => wdVenues || [], [wdVenues]);
  const windows = useMemo(() => wdWindows || {}, [wdWindows]);
  const dayOpens = useMemo(() => wdOpens || {}, [wdOpens]);
  const packageSpan = wdSpan || null;
  const limits = useMemo(() => wdLimits || {}, [wdLimits]);
  const family = useMemo(() => wdFamily || {}, [wdFamily]);
  const fieldErrors = useMemo(() => wdFieldErrors || {}, [wdFieldErrors]);
  const changeLog = useMemo(() => wdChangeLog || [], [wdChangeLog]);
  const interviews = useMemo(() => wdInterviews || [], [wdInterviews]);
  const interviewsRef = useRef(interviews);
  interviewsRef.current = interviews;
  const ritualOptions = useMemo(() => wdRitualOptions || [], [wdRitualOptions]);
  const contacts = useMemo(() => wdContacts || [], [wdContacts]);

  const submitted = !!doc.submitted;
  const closed = !!doc.locked;
  // This side is done, the form is closed, or this is a staff read.
  const locked = submitted || closed || isReadOnly();
  // Either side submitting closes the three shared sections for both of them.
  const sharedLocked = locked || !!wdSharedLocked;

  // Async paths read these rather than the render-time values, which are stale
  // by the time a response lands.
  const docRef = useRef(doc);
  docRef.current = doc;
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const errorsRef = useRef(fieldErrors);
  errorsRef.current = fieldErrors;
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const sharedLockedRef = useRef(sharedLocked);
  sharedLockedRef.current = sharedLocked;

  // --- flow, derived entirely from the payload ---------------------------

  const index = Math.max(0, sections.findIndex((s) => s.key === wdSection));
  const section = sections[index] || null;
  const prevKey = index > 0 ? sections[index - 1].key : null;
  const nextKey = index < sections.length - 1 ? sections[index + 1]?.key : null;
  const activeDate = resolveDate(events, wdActiveDate);

  // --- write plumbing ----------------------------------------------------

  // Day Notes is the one section the partner never sees, so it stays writable
  // after they have submitted.
  const isPrivateSection = useCallback((sectionKey) => {
    const found = (sectionsRef.current || []).find((s) => s.key === sectionKey);
    if (!found) return false;
    // Day Notes is not private as a section any more: the one private thing
    // in it is a surprise, and a surprise is an activity, not a field.
    return found.scope === 'private';
  }, []);

  const canWrite = useCallback(
    () => !docRef.current.submitted && !isReadOnly(),
    []
  );

  const canWriteShared = useCallback(
    () => canWrite() && !sharedLockedRef.current,
    [canWrite]
  );

  // After the first submit the shared sections close, but venue, map link and
  // end time stay reachable as edit requests: the server answers those with
  // needs_approval rather than refusing them.
  const canRequest = useCallback(
    () => !isReadOnly() && !docRef.current.locked,
    []
  );

  // No deep link means there is no brief behind the page, so there is nothing
  // to ask the server about either.
  const hasBrief = useCallback(() => {
    const { briefUuid: uuid } = getBriefIds();
    return !!uuid;
  }, []);

  const lockLocal = useCallback(
    (extra) => {
      const shut = { ...docRef.current, submitted: true, ...extra };
      docRef.current = shut;
      setWdDoc(shut);
      return shut;
    },
    [setWdDoc]
  );

  const setFieldError = useCallback(
    (key, message) => {
      const next = { ...errorsRef.current };
      if (message) next[key] = message;
      else delete next[key];
      errorsRef.current = next;
      setWdFieldErrors(next);
    },
    [setWdFieldErrors]
  );

  /**
   * One write, with the retry ladder and the shared rejection handling. `key`
   * keeps each in-flight write on its own timer, so a timing retry cannot
   * cancel a text one. `shared` says which lock a brief_submitted rejection
   * means: the shared sections, or this side's whole form. `patched` says
   * the call takes a partial body: it is handed everything still owed on
   * the key, rather than only what this write changed. `onGiveUp` runs
   * once the retries are spent, for a caller still waiting on an answer.
   */
  const send = useCallback(
    (key, call, onOk, onReject, shared, patched, onGiveUp) => {
      const mark = (state) => {
        if (state === 'idle') delete keyStates[key];
        else keyStates[key] = state;
        if (state !== 'failed') delete failedWrites[key];
        setWdSaveState(worstState());
      };
      mark('saving');
      // Counted before the request leaves, so a read already on its way
      // can tell that it was overtaken.
      writeStamp.count += 1;
      // A newer write on this key takes over from one waiting on a retry:
      // the wait is dropped and that ladder ends here, so nothing goes on
      // waiting on it.
      if (retryTimers[key]) {
        clearTimeout(retryTimers[key].timer);
        retryTimers[key].settle();
        delete retryTimers[key];
      }
      // One promise for the whole ladder, retries and the waits between
      // them included. A read or a write that waits on this key waits for
      // the answer, not only the first try: a poll landing in the gap put
      // the server's older copy back, and the next edit sent it.
      let settle;
      const ladder = new Promise((resolve) => { settle = resolve; });
      const attempt = (n) => {
        const body = patched ? owedPatch[key] : undefined;
        return call(body)
          .then((res) => {
            // Only what this attempt carried is settled. A write merged in
            // while it was away is a newer object and is still owed.
            if (patched && owedPatch[key] === body) delete owedPatch[key];
            mark('idle');
            if (onOk) onOk(res);
            settle();
          })
          .catch((err) => {
            if (err && err.rejected) {
              // Refused rather than lost: the caller puts the screen back,
              // so none of it is owed any more.
              if (patched) delete owedPatch[key];
              // Stale local state: the form is closed server side. Close it
              // here too rather than reporting a save failure.
              if (err.code === REJECT.SUBMITTED) {
                if (shared) setWdSharedLocked(true);
                else lockLocal();
                mark('idle');
                settle();
                return;
              }
              // Retrying sends the same body, so the ladder would only repeat
              // the refusal.
              mark(HANDLED_REJECTS.includes(err.code) ? 'idle' : 'rejected');
              if (onReject) onReject(err);
              settle();
              return;
            }
            // Out of retries nothing is trying any more, so the banner must
            // stop saying it is.
            if (n >= RETRY_DELAYS.length) {
              mark('failed');
              failedWrites[key] = () =>
                send(key, call, onOk, onReject, shared, patched, onGiveUp);
              if (onGiveUp) onGiveUp();
              settle();
              return;
            }
            mark('error');
            // Gone from the timers once it fires. The ladder stays in
            // flight across the wait, so nothing reads past it meanwhile.
            retryTimers[key] = {
              timer: setTimeout(() => {
                delete retryTimers[key];
                attempt(n + 1);
              }, RETRY_DELAYS[n]),
              settle,
            };
          });
      };
      // Returned, and remembered, so a caller whose own write only makes
      // sense after this one can wait for it rather than race it.
      inFlight[key] = ladder;
      ladder.then(() => {
        if (inFlight[key] === ladder) delete inFlight[key];
      });
      attempt(0);
      return ladder;
    },
    [lockLocal, setWdSaveState, setWdSharedLocked]
  );

  // --- reading the server's copy back ------------------------------------

  const applyPayload = useCallback(
    (res, { keepSection, keepFamily } = {}) => {
      // One link is handed out for both apps, and the server says which one
      // this reader belongs in: the couple and our own team read it in the
      // customer app, a professional we have put on the booking reads it in
      // theirs. Moving before anything is drawn keeps the wrong app from
      // flashing the form first.
      if (res.form_url && movedHome(res.form_url)) return;
      setWdSections(res.sections);
      setWdEvents(res.events);
      setWdVenues(res.venues);
      setWdWindows(res.package_windows);
      setWdOpens(res.package_opens);
      setWdSpan(res.package_span);
      setWdLocksAt(res.locks_at || null);
      setWdClosed(!!res.closed);
      setWdWantsInterview(res.wants_interview);
      // Whether the question is this form's to ask. Settled on the load and
      // kept, so answering it does not take the stop away mid-walk: the
      // couple reads what the answer reveals, then moves on themselves.
      if (res.wants_interview === null) setWdAskInterview(true);
      setWdInterviewQuestion(res.interview_question);
      setWdRitualOptions(res.ritual_options);
      setWdContacts(res.contacts);
      setWdNeedsInterview(res.needs_interview);
      setWdInterviews(res.interviews);
      setWdLimits(res.limits);
      if (!keepFamily) setWdFamily(withSessionPictures(res.family));
      setWdBookingName(res.booking_name || '');
      setWdRole(res.role);
      setWdSharedLocked(res.shared_locked);
      // The log's own source is the status endpoint, so an empty one here is
      // absence rather than news.
      if (res.change_log.length) setWdChangeLog(res.change_log);
      // Submitted or closed: the same flag every write guard reads.
      setPayloadReadOnly(res.read_only);
      setWdCanEdit(res.can_edit !== false);
      eventsRef.current = res.events;
      docRef.current = res.answers;
      setWdDoc(res.answers);
      if (keepSection) return;
      // Resume where they left off, falling back to the first section.
      const resume = res.sections.some((s) => s.key === res.current_section)
        ? res.current_section
        : res.sections[0]?.key;
      setWdSection(resume || null);
    },
    [setWdBookingName, setWdCanEdit, setWdChangeLog, setWdDoc, setWdEvents, setWdFamily,
      setWdContacts, setWdInterviews, setWdNeedsInterview, setWdRitualOptions,
      setWdLimits, setWdRole, setWdSection, setWdSections,
      setWdSharedLocked, setWdVenues, setWdWindows, setWdOpens, setWdSpan,
      setWdLocksAt, setWdClosed,
      setWdWantsInterview, setWdAskInterview, setWdInterviewQuestion]
  );

  /**
   * Who changed what. The bootstrap carries the form; the change log is the
   * status endpoint's, so it is asked for separately and its failure is
   * harmless.
   */
  const loadChangeLog = useCallback(() => {
    if (!hasBrief()) return;
    fetchStatus(getBriefIds())
      .then((res) => setWdChangeLog((res && res.change_log) || []))
      .catch(() => {});
  }, [hasBrief, setWdChangeLog]);

  /**
   * Pulls the server's copy back in place of this one. The partner writes the
   * same document, so their edits arrive this way rather than by reload.
   * Anything still being typed is left alone: a pending debounce is flushed
   * before the refresh, never overwritten by it.
   */
  const refresh = useCallback(() => {
    if (!hasBrief()) return Promise.resolve(null);
    // Nothing the server is still owed gets overtaken. A read started while
    // a write is on its way answers from before it, so applying it puts the
    // old value back on screen and the edit only returns on the next poll.
    // Typing still held back is left to its own timer rather than flushed:
    // the held write reads the document at the moment it fires, so letting
    // the read land first would have it post the server's older copy.
    if (Object.keys(pendingText).length) return Promise.resolve(null);
    const stamp = familyStamp.count;
    const flying = Object.keys(inFlight).map((key) => inFlight[key]);
    return Promise.all(flying).then(() => {
      // Read after the waiting is done, not before it: the count has to be
      // the one this request is actually made with.
      const wrote = writeStamp.count;
      return bootstrap(getBriefIds()).then((res) => [res, wrote]);
    })
      .then(([res, wrote]) => {
        if (!res || res.denied) return null;
        // Answered from before a write that has gone out since. Applying it
        // would put the old value back on screen until that write returned,
        // which is the card emptying itself. The next poll or focus reads
        // again, by which time the write has landed.
        // Typing held back since the read left counts the same way: nothing
        // has gone out yet to move the count, so a link pasted while this
        // read was travelling was wiped off the card when it landed.
        if (writeStamp.count !== wrote || Object.keys(pendingText).length) {
          return null;
        }
        applyPayload(res, {
          keepSection: true,
          keepFamily: familyStamp.count !== stamp,
        });
        loadChangeLog();
        return res;
      })
      // A failed refresh changes nothing on screen: the local copy stands and
      // the next poll or focus tries again.
      .catch(() => null);
  }, [applyPayload, hasBrief, loadChangeLog]);

  // --- answers on the field sections -------------------------------------

  const push = useCallback(
    (nextDoc, sectionKey) => {
      send(
        `section:${sectionKey}`,
        () =>
          patchSection({
            ...getBriefIds(),
            sectionKey,
            payload: groupOf(nextDoc, sectionKey),
            doc: nextDoc,
          }),
        null,
        null,
        !isPrivateSection(sectionKey)
      );
    },
    [isPrivateSection, send]
  );

  // `sectionKey` and `fieldKey` are the server's own keys, used verbatim as the
  // storage keys in the PATCH body.
  const write = useCallback(
    (sectionKey, fieldKey, value, debounced) => {
      if (!canWrite()) return;
      // A shared section closes for both sides on the first submit; a private
      // one stays open for its own side.
      if (!isPrivateSection(sectionKey) && sharedLockedRef.current) return;
      const stamped = {
        ...docRef.current,
        [sectionKey]: { ...groupOf(docRef.current, sectionKey), [fieldKey]: value },
        revision: docRef.current.revision + 1,
        current_section: sectionKey,
        section_updated_at: {
          ...docRef.current.section_updated_at,
          [sectionKey]: new Date().toISOString(),
        },
      };
      docRef.current = stamped;
      setWdDoc(stamped);

      if (!debounced) {
        push(stamped, sectionKey);
        return;
      }
      holdText(sectionKey, () => push(docRef.current, sectionKey),
               TEXT_DEBOUNCE_MS);
    },
    [canWrite, isPrivateSection, push, setWdDoc]
  );

  // --- event entries -----------------------------------------------------

  const replaceEntries = useCallback(
    (incoming) => {
      const list = Array.isArray(incoming) ? incoming : [incoming];
      const byId = {};
      list.forEach((entry) => {
        if (entry && entry.id !== undefined) byId[String(entry.id)] = entry;
      });
      const next = eventsRef.current.map((entry) => byId[String(entry.id)] || entry);
      eventsRef.current = next;
      setWdEvents(next);
    },
    [setWdEvents]
  );

  const setEntries = useCallback(
    (next) => {
      eventsRef.current = next;
      setWdEvents(next);
    },
    [setWdEvents]
  );

  // Everything still waiting on a typing pause, sent now. Each one goes out
  // the way it would have gone out on its own.
  const flushText = useCallback(() => {
    Object.keys(pendingText).forEach((key) => {
      const held = pendingText[key];
      clearTimeout(held.timer);
      delete pendingText[key];
      held.run();
    });
  }, []);

  const setText = useCallback(
    (sectionKey, fieldKey, value) => write(sectionKey, fieldKey, value, true),
    [write]
  );

  // Selections and toggles save on tap.
  const setValue = useCallback(
    (sectionKey, fieldKey, value) => write(sectionKey, fieldKey, value),
    [write]
  );

  /**
   * The full order for one date, both sides together. The response says which
   * entries lost their timing, because the day they were set against has
   * changed.
   */
  const reorder = useCallback(
    (date, order) => {
      if (!canWriteShared()) return;
      const rank = {};
      order.forEach((id, i) => {
        rank[String(id)] = i;
      });
      const before = eventsRef.current;
      const day = before
        .filter((x) => x.date === date)
        .sort((a, b) => (rank[String(a.id)] ?? 0) - (rank[String(b.id)] ?? 0));
      // The day's entries are dealt back into the positions they already hold,
      // so the rest of the payload's order is untouched.
      const queue = day.slice();
      const next = before.map((x) => (x.date === date ? queue.shift() : x));
      setEntries(next);

      send(
        `order:${date}`,
        () => patchEventOrder({ ...getBriefIds(), order }),
        (res) => {
          if (!res) return;
          if (res.events) {
            setEntries(res.events);
            return;
          }
          // The server decides what the move invalidated, so the cleared list
          // is applied rather than guessed at from the moved position.
          const cleared = (res.cleared || []).map(String);
          if (!cleared.length) return;
          setEntries(
            eventsRef.current.map((x) =>
              cleared.indexOf(String(x.id)) >= 0
                ? { ...x, start: '', end: '', timing_status: TIMING.UNSET }
                : x
            )
          );
        },
        () => setEntries(before),
        true
      );
    },
    [canWriteShared, send, setEntries]
  );

  /**
   * A custom ritual, inserted where the couple put it. The server answers with
   * the day's entries, so the new one arrives with its real id.
   */
  const addRitual = useCallback(
    (date, name, sideKey, position) => {
      if (!canWriteShared() || !name.trim()) return;
      const dayIds = eventsRef.current
        .filter((x) => x.date === date)
        .map((x) => String(x.id));
      send(
        `order:${date}`,
        () =>
          patchEventOrder({
            ...getBriefIds(),
            order: dayIds,
            addRitual: { name: name.trim(), side: sideKey, position },
          }),
        (res) => {
          if (res && res.events) setEntries(res.events);
          else refresh();
        },
        (err) => setWdToast(err.message),
        true
      );
    },
    [canWriteShared, refresh, send, setEntries, setWdToast]
  );

  const dismissToast = useCallback(() => setWdToast(''), [setWdToast]);

  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;

  const setContact = useCallback(
    (side, kind, patch) => {
      if (!canWriteShared()) return;
      // Kept on screen while the write is in flight, like every other field.
      setWdContacts(
        contactsRef.current.map((row) =>
          row.side === side && row.kind === kind ? { ...row, ...patch } : row
        )
      );
      // Owed before the wait, so a second field typed inside it is sent
      // with the first rather than in place of it.
      owe(`contact:${side}:${kind}`, patch);
      holdText(
        `contact:${side}:${kind}`,
        () => send(
          `contact:${side}:${kind}`,
          (payload) => patchContact({ ...getBriefIds(), side, kind, payload }),
          (res) => {
            if (!res || !res.contacts) return;
            // The server saves each field trimmed. Taking that back over a
            // field still being typed took the space off the end of a name
            // before the next word could follow it, so a field that differs
            // only by that keeps what is on screen.
            const typed = contactsRef.current;
            setWdContacts(res.contacts.map((row) => {
              const mine = typed.find(
                (t) => t.side === row.side && t.kind === row.kind);
              if (!mine) return row;
              const kept = {};
              Object.keys(row).forEach((key) => {
                if (typeof row[key] === 'string' && typeof mine[key] === 'string'
                    && mine[key] !== row[key] && mine[key].trim() === row[key]) {
                  kept[key] = mine[key];
                }
              });
              return { ...row, ...kept };
            }));
          },
          null,
          true,
          true
        ),
        FIELD_DEBOUNCE_MS
      );
    },
    [canWriteShared, send, setWdContacts]
  );

  const setInterview = useCallback(
    (side, group, patch) => {
      if (!canWriteShared()) return;
      const mine = (row) => row.side === side && row.group === group;
      const before = interviewsRef.current;
      // Shown as answered straight away, the same as every other card on
      // the form. Waiting for the round trip left the sitting reading as
      // unanswered for a second or two after they had answered it, which
      // reads as the click not having registered.
      setWdInterviews(before.map(
        (row) => (mine(row) ? { ...row, ...patch } : row)));
      owe(`interview:${side}:${group}`, patch);
      send(
        `interview:${side}:${group}`,
        (payload) => patchInterview({ ...getBriefIds(), side, group, payload }),
        (res) => {
          if (res && res.interviews) setWdInterviews(res.interviews);
          // Making room for the sitting can start the day's first event
          // later, so the timeline is read back rather than left showing
          // hours the server no longer holds.
          if (res && res.moved) refresh();
        },
        (err) => {
          // Only this sitting goes back. The others are answers of their
          // own and are not this refusal's to undo.
          const was = before.find(mine);
          setWdInterviews(interviewsRef.current.map(
            (row) => (mine(row) && was ? was : row)));
          setWdToast(err.message);
        },
        true,
        true
      );
    },
    [canWriteShared, refresh, send, setWdInterviews, setWdToast]
  );

  // Anything on the day can be taken off it, a package event included: the
  // server decides whether that is allowed, not this.
  const removeRitual = useCallback(
    (entryId) => {
      if (!canWriteShared()) return;
      const entry = entryById(eventsRef.current, entryId);
      if (!entry) return;
      const before = eventsRef.current;
      const order = before
        .filter((x) => x.date === entry.date)
        .map((x) => String(x.id))
        .filter((id) => id !== String(entryId));
      setEntries(before.filter((x) => String(x.id) !== String(entryId)));
      send(
        `order:${entry.date}`,
        () => patchEventOrder({ ...getBriefIds(), order, removeEntryId: entryId }),
        (res) => {
          if (res && res.events) setEntries(res.events);
        },
        () => setEntries(before),
        true
      );
    },
    [canWriteShared, send, setEntries]
  );

  // What the event is called on their day. Committed on Done rather than while
  // typing, so a half typed name never reaches the other side.
  const renameEvent = useCallback(
    (entryId, name) => {
      if (!canWriteShared()) return;
      const before = entryById(eventsRef.current, entryId);
      if (!before || (before.name || '') === name) return;
      replaceEntries({ ...before, name });
      send(
        `name:${entryId}`,
        () => patchEventName({ ...getBriefIds(), entryId, name }),
        (res) => {
          if (res && res.entry) replaceEntries(res.entry);
        },
        () => replaceEntries(before),
        true
      );
    },
    [canWriteShared, replaceEntries, send]
  );

  /**
   * Where the couple shot sits against its event. Only two places are open to
   * it, the start of that event or the end of it, and it moves with the event
   * when the day is reordered, so nothing else here has to follow it.
   */
  const setCoupleShot = useCallback(
    (entryId, position) => {
      if (!canWriteShared()) return;
      const before = entryById(eventsRef.current, entryId);
      if (!before || !before.couple_shot) return;
      if (before.couple_shot.position === position) return;
      replaceEntries({ ...before, couple_shot: { ...before.couple_shot, position } });
      send(
        `coupleshot:${entryId}`,
        () => patchCoupleShot({ ...getBriefIds(), entryId, position }),
        (res) => {
          // Making room for the shot moves the event's own hours, so the
          // whole card comes back rather than the shot alone.
          if (res && res.entry) replaceEntries(res.entry);
          else if (res && res.couple_shot) {
            const current = entryById(eventsRef.current, entryId);
            if (current) replaceEntries({ ...current, couple_shot: res.couple_shot });
          }
        },
        () => replaceEntries(before),
        true
      );
    },
    [canWriteShared, replaceEntries, send]
  );

  /**
   * Whether this couple wants the interviews. The creative preferences form
   * asks the same question, and both forms read the same answer, so it can
   * be answered from whichever one they reach first.
   */
  const setWantsInterview = useCallback(
    (wanted) => {
      if (!canWriteShared()) return;
      const before = wdWantsInterview;
      setWdWantsInterview(wanted);
      send(
        'interviewchoice',
        () => patchInterviewChoice({ ...getBriefIds(), wantsInterview: wanted }),
        (res) => {
          if (res && Array.isArray(res.interviews)) setWdInterviews(res.interviews);
          setWdNeedsInterview(wanted === true);
        },
        () => setWdWantsInterview(before),
        true
      );
    },
    [canWriteShared, send, setWdInterviews, setWdNeedsInterview,
      setWdWantsInterview, wdWantsInterview]
  );

  /**
   * The half hour a shot taken inside its event runs in. Only the server can
   * say whether it fits, so the card waits for what comes back rather than
   * drawing a time the event may not hold.
   */
  const setCoupleShotTime = useCallback(
    (entryId, start) => {
      if (!canWriteShared()) return;
      const before = entryById(eventsRef.current, entryId);
      if (!before || !before.couple_shot) return;
      send(
        `shotat:${entryId}`,
        () => patchCoupleShot({ ...getBriefIds(), entryId, start }),
        (res) => {
          if (res && res.couple_shot) {
            const current = entryById(eventsRef.current, entryId);
            if (current) replaceEntries({ ...current, couple_shot: res.couple_shot });
          }
        },
        null,
        true
      );
    },
    [canWriteShared, replaceEntries, send]
  );

  /**
   * That the couple has closed this card, or reopened it from the pencil.
   * Nothing on the form is compulsory, so closing a card is the answer and
   * the progress they are shown counts it, whether or not every field on it
   * was filled. Stored rather than kept in the page, so a reload resumes
   * where they left off.
   */
  const setEventConfirmed = useCallback(
    (entryId, confirmed) => {
      if (!canWriteShared()) return;
      const before = entryById(eventsRef.current, entryId);
      if (!before || !!before.confirmed === !!confirmed) return;
      replaceEntries({ ...before, confirmed: !!confirmed });
      setFieldError(`entry:${entryId}`, '');
      // The card's own fields are saved separately, and the map link waits
      // for a gap in the typing before it goes. Closing the card before that
      // landed would ask the server to check an event it has not been told
      // about yet, and be refused for a field that is in fact filled.
      send(
        `confirmed:${entryId}`,
        () =>
          settled(`timing:${entryId}`).then(() =>
            patchEventConfirmed({ ...getBriefIds(), entryId, confirmed })
          ),
        null,
        (err) => {
          // Only the closing is undone. Anything answered while this was
          // away is an answer of its own, and reverting the whole card to
          // the snapshot this call was made with would take it back off.
          const current = entryById(eventsRef.current, entryId) || before;
          replaceEntries({ ...current, confirmed: !!before.confirmed });
          // Settling a card the couple has not finished is a refusal they
          // can act on, so it belongs on the card and not in a banner that
          // says the save broke.
          if (err.code === REJECT.ENTRY_INCOMPLETE) {
            setFieldError(`entry:${entryId}`, err.message);
          }
        },
        true
      );
    },
    [canWriteShared, replaceEntries, send, setFieldError]
  );

  /**
   * Whose event this is, where the payload says the couple may still decide.
   * A side the estimation sold comes back as side_locked, which the card shows
   * in the server's own words rather than guessing at the reason.
   */
  const setEventSide = useCallback(
    (entryId, side) => {
      if (!canWriteShared()) return;
      const before = entryById(eventsRef.current, entryId);
      if (!before || before.side === side) return;
      replaceEntries({ ...before, side });
      setFieldError(`side:${entryId}`, '');
      send(
        `side:${entryId}`,
        () => patchEventSide({ ...getBriefIds(), entryId, side }),
        (res) => {
          if (res && res.side) replaceEntries({ ...entryById(eventsRef.current, entryId), side: res.side });
        },
        (err) => {
          replaceEntries(before);
          setFieldError(`side:${entryId}`, err.message);
        },
        true
      );
    },
    [canWriteShared, replaceEntries, send, setFieldError]
  );

  /**
   * One event's date, start, end or venue. Nothing saves here: the server
   * records the change as a request and answers with what is awaiting review,
   * so the approved schedule on the card is left exactly as it was. A clash or
   * an unknown venue is still a refusal, and says so against the field.
   */
  const setTiming = useCallback(
    (entryId, patch) => {
      // The fields are read only once either side has submitted, so this is
      // the client agreeing with the server rather than a second gate.
      if (!canRequest() || sharedLockedRef.current) return;
      const before = entryById(eventsRef.current, entryId);
      if (!before) return;
      const asked = { ...before, ...patch };
      // Saved as answered, so the field keeps what was typed while the write
      // is in flight. The response still owns the outcome.
      replaceEntries(asked);
      setFieldError(`entry:${entryId}`, '');
      setFieldError(`entry:${entryId}:venue`, '');
      setFieldError(`entry:${entryId}:map_link`, '');

      // The map link is typed, so it waits for a gap rather than sending a
      // request per keystroke. A date, a time and now the venue are picks,
      // and go at once.
      const typed = Object.keys(patch).every((key) => key === 'map_link');
      const key = `timing:${entryId}`;
      if (pendingText[key]) clearTimeout(pendingText[key].timer);
      delete pendingText[key];
      if (typed) {
        holdText(key, () => flush(), FIELD_DEBOUNCE_MS);
        return;
      }
      flush();

      function flush() {
      return send(
        `timing:${entryId}`,
        () =>
          patchEventTiming({
            ...getBriefIds(),
            entryId,
            // The venue is the catalogue row the crew is sent to, with the
            // name it reads by beside it. The map link is the couple's own
            // text.
            payload: {
              date: asked.date,
              start: asked.start || '',
              end: asked.end || '',
              venue_id: asked.venue_id || null,
              venue: asked.venue || '',
              // Left out, the server keeps the link it last saved.
              ...(!typed && refusedLinks[entryId]
                ? {} : { map_link: asked.map_link || '' }),
            },
          }),
        (res) => {
          if (!res) return;
          if (typed) delete refusedLinks[entryId];
          // Making room for what runs in front of an event starts it
          // later, so the timeline is read back whole rather than left
          // showing hours the server no longer holds.
          if (res.moved) {
            refresh();
            return;
          }
          // The whole entry comes back only where the server still owns the
          // value outright, such as a reopened form.
          if (res.entry) {
            // Anything answered since this write left is kept. The couple
            // pastes the map link while the venue is still travelling, and
            // the venue's answer carries the entry as the server knew it,
            // which is without that link: taking it wholesale put the
            // field back to empty until the link's own write returned.
            // The card being closed is one of those answers: confirmed just
            // after an edit, this reply still said open, and reopened it.
            const current = entryById(eventsRef.current, entryId);
            const newer = {};
            if (current) {
              ['date', 'start', 'end', 'venue_id', 'venue', 'map_link',
                'confirmed']
                .forEach((field) => {
                  if (current[field] !== asked[field]) {
                    newer[field] = current[field];
                  }
                });
            }
            // A refused link was left out of this write, so the entry comes
            // back with the saved one, which is not what is being typed.
            if (refusedLinks[entryId] && current) newer.map_link = current.map_link;
            replaceEntries({ ...res.entry, ...newer });
            return;
          }
          // Saved either way: the answer stays on the card, with a note of
          // what ops has yet to approve.
          if (res.pending) {
            // The card as it stands now, not as it stood when this write
            // left. The couple moves to the next field while this one is
            // still travelling, so answering with the entry this call
            // captured would take that next answer back off the card until
            // its own write returned.
            const current = entryById(eventsRef.current, entryId) || asked;
            replaceEntries({ ...current, pending: res.pending,
                             timing_status: TIMING.PENDING });
          }
        },
        (err) => {
          // A link refused while it is still being typed stays in the field
          // with the reason under it. Putting back the reading from before
          // the last keystroke took letters off what the couple was typing,
          // so a link only ever went in by pasting it whole.
          const typing = err.code === REJECT.BAD_MAP_LINK && typed;
          if (typing) refusedLinks[entryId] = true;
          // Only what this write asked for is put back. Anything answered
          // since belongs to a write of its own and is not this refusal's
          // to undo.
          if (!typing) {
            const current = entryById(eventsRef.current, entryId) || before;
            const undone = { ...current };
            Object.keys(patch).forEach((field) => { undone[field] = before[field]; });
            replaceEntries(undone);
          }
          // Said under the field it is about. The rest are about the card
          // as a whole, its times or whether it can close, and are said once
          // for the card.
          if (err.code === REJECT.BAD_MAP_LINK) {
            setFieldError(`entry:${entryId}:map_link`, err.message);
          } else if (err.code === REJECT.UNKNOWN_VENUE) {
            setFieldError(`entry:${entryId}:venue`, err.message);
          } else if (
            err.code === REJECT.CONFLICT ||
            err.code === REJECT.LIMIT ||
            err.code === REJECT.ENTRY_INCOMPLETE ||
            err.code === REJECT.BEFORE_PACKAGE ||
            err.code === REJECT.AFTER_PACKAGE ||
            err.code === REJECT.TOO_LONG
          ) {
            setFieldError(`entry:${entryId}`, err.message);
          }
        },
        true
      );
      }
    },
    [canRequest, refresh, replaceEntries, send, setFieldError]
  );

  // --- family photographs ------------------------------------------------

  const familyRef = useRef(family);
  familyRef.current = family;

  const mergeFamily = useCallback(
    (sideKey, patch) => {
      const current = familyRef.current || {};
      const next = { ...current, [sideKey]: { ...(current[sideKey] || {}), ...patch } };
      familyRef.current = next;
      familyStamp.count += 1;
      setWdFamily(next);
    },
    [setWdFamily]
  );

  /**
   * Add one family photograph: detect on it here, and send only the boxes.
   *
   * The picture itself stays on the device. It is kept in memory for as long
   * as the page is open, because the crops are cut from it when the couple
   * picks a face, and it is dropped when they leave. Resolves once this one
   * is done either way, so a batch can wait for it before the next.
   */
  const uploadPhoto = useCallback(
    (sideKey, file) => {
      if (!canWriteShared() || !file) return Promise.resolve();
      const preview = URL.createObjectURL(file);
      const before = familyRef.current[sideKey] || {};
      // On screen before anything is read. Detecting holds the main thread
      // for a moment, and a side that already has a photograph would
      // otherwise show nothing at all until the server answered, which
      // reads as a picker that did not take the file.
      mergeFamily(sideKey, {
        photos: (before.photos || []).concat([{
          photo_id: null,
          photo_url: preview,
          detection_status: DETECTION.RUNNING,
          face_count: 0,
          tags: [],
        }]),
        photo_url: preview,
        detection_status: DETECTION.RUNNING,
        tags: before.tags || [],
      });
      setFieldError(`family:${sideKey}`, '');

      return readImage(file)
        .then((bitmap) =>
          // Dropped as soon as it has been looked at, whether or not the
          // detector found anything.
          findFaces(bitmap)
            .then((faces) => { releaseImage(bitmap); return { faces }; })
            .catch((err) => { releaseImage(bitmap); throw err; }))
        .then(({ faces }) => {
          if (!faces.length) throw new Error(NO_FACES);
          // No brief means no row to hang a face off, so the review route
          // keeps what was found on screen and writes nothing.
          if (!hasBrief()) {
            mergeFamily(sideKey, {
              detection_status: DETECTION.DONE,
              tags: faces.map((bbox, i) => ({
                id: `preview-${i}`, bbox, is_selected: false, relationship: '',
              })),
            });
            return null;
          }
          return postPhotoFaces({ ...getBriefIds(), side: sideKey, faces })
            .then((res) => {
              if (!res || !res.family) return res;
              // Held against the row the server just made, so a crop can
              // still be cut out of it later in the session and the cards
              // can go on drawing it.
              pictures[String(res.photo_id)] = { file, url: preview };
              const next = withSessionPictures(res.family);
              familyRef.current = next;
              familyStamp.count += 1;
              setWdFamily(next);
              return res;
            });
        })
        .catch((err) => {
          URL.revokeObjectURL(preview);
          // Only this photograph comes off. The side as it stood when this
          // one started would take any that landed since along with it.
          const now = familyRef.current[sideKey] || {};
          const kept = (now.photos || []).filter((p) => p.photo_url !== preview);
          const last = kept[kept.length - 1] || {};
          mergeFamily(sideKey, {
            photos: kept,
            photo_url: last.photo_url || '',
            detection_status: DETECTION.FAILED,
          });
          setFieldError(`family:${sideKey}`, err.message);
        });
    },
    [canWriteShared, hasBrief, mergeFamily, setFieldError, setWdFamily]
  );

  /**
   * Take one photograph off a side. The server answers with the side's panels
   * as they stand afterwards. The faces nobody picked leave with it; the
   * ones they did were cut and stored already, and those stay on the side
   * and go on counting against the cap.
   */
  const removePhoto = useCallback(
    (sideKey, photoId) => {
      if (!canWriteShared() || !photoId) return;
      const before = familyRef.current[sideKey];
      // Gone at once: waiting on the round trip reads as a click that did
      // nothing. The failure path puts it back.
      const kept = (before.photos || []).filter(
        (p) => String(p.photo_id) !== String(photoId)
      );
      const gone = (before.photos || []).find(
        (p) => String(p.photo_id) === String(photoId)
      ) || {};
      // A face they picked has been cut and stored, and the server keeps it
      // when the photograph goes. Carrying it over here too stops it
      // blinking off the screen for the length of the round trip.
      const survives = (gone.tags || []).filter((t) => t.is_selected && t.src);
      const last = kept[kept.length - 1] || {};
      const held = pictures[String(photoId)];
      if (held) {
        URL.revokeObjectURL(held.url);
        delete pictures[String(photoId)];
      }
      mergeFamily(sideKey, {
        photos: kept,
        tags: kept.reduce((all, p) => all.concat(p.tags || []), [])
          .concat(survives),
        photo_id: last.photo_id || null,
        photo_url: last.photo_url || '',
        detection_status: last.detection_status || null,
      });
      setFieldError(`family:${sideKey}`, '');
      send(
        `photo:${sideKey}`,
        () => deletePhoto({ ...getBriefIds(), photoId }),
        (res) => {
          if (res && res.family) {
            const next = withSessionPictures(res.family);
            familyRef.current = next;
            familyStamp.count += 1;
            setWdFamily(next);
          }
        },
        (err) => {
          mergeFamily(sideKey, before);
          setFieldError(`family:${sideKey}`, err.message);
        },
        true
      );
    },
    [canWriteShared, mergeFamily, send, setFieldError, setWdFamily]
  );

  /**
   * Cut one picked face out of its photograph and put it in the bucket.
   *
   * This is the only image the couple's device ever sends. It needs the
   * picture it came from, which is only in memory, so a face picked after a
   * reload says so rather than storing a blank.
   */
  const sendCrop = useCallback(
    (sideKey, tag) => {
      const held = pictures[String(tag.photo_id)];
      if (!held) {
        setFieldError(`family:${sideKey}`, NEEDS_PHOTO);
        return;
      }
      cropFace(held.file, tag.bbox)
        .then((blob) =>
          createCropSlot({ ...getBriefIds(), tagId: tag.id }).then((slot) => {
            if (!slot || !slot.upload) return null;
            return uploadToS3({ upload: slot.upload, file: blob }).then(() =>
              finishCrop({ ...getBriefIds(), tagId: tag.id })
            );
          })
        )
        .then((res) => {
          if (!res || !res.family) return;
          const next = withSessionPictures(res.family);
          familyRef.current = next;
          familyStamp.count += 1;
          setWdFamily(next);
        })
        .catch((err) => setFieldError(`family:${sideKey}`, err.message));
    },
    [setFieldError, setWdFamily]
  );

  /**
   * Only the selection and the relationship are ever written: the boxes are
   * the detector's. The cap is the payload's, and the server enforces it, so a
   * limit_exceeded rejection still has to be shown.
   */
  const setFace = useCallback(
    (sideKey, tagId, patch) => {
      if (!canWriteShared()) return;
      // One face sits in two lists: the side's own, which the crops below
      // are drawn from, and its photograph's, which the box on the picture
      // is drawn from. Both change together, or the box keeps its old colour
      // until the next full read.
      const swapTag = (panel, id, fn) => ({
        tags: (panel.tags || []).map((t) => (String(t.id) === String(id) ? fn(t) : t)),
        photos: (panel.photos || []).map((photo) => ({
          ...photo,
          tags: (photo.tags || []).map((t) => (String(t.id) === String(id) ? fn(t) : t)),
        })),
      });
      const current = familyRef.current[sideKey] || {};
      const before = current.tags || [];
      const beforePhotos = current.photos || [];
      mergeFamily(sideKey, swapTag(current, tagId, (t) => ({ ...t, ...patch })));
      setFieldError(`family:${sideKey}`, '');
      owe(`face:${tagId}`, patch);
      send(
        `face:${tagId}`,
        (payload) => patchFace({ ...getBriefIds(), tagId, payload }),
        (res) => {
          if (res && res.tag) {
            mergeFamily(sideKey, swapTag(
              familyRef.current[sideKey] || {}, res.tag.id, () => res.tag));
          }
          // A face nobody picked is never cut, so the crop follows the
          // choice rather than the detection.
          if (res && res.needs_crop) {
            const picked = (familyRef.current[sideKey].tags || []).find(
              (t) => String(t.id) === String(tagId)
            );
            if (picked) sendCrop(sideKey, picked);
          }
        },
        (err) => {
          mergeFamily(sideKey, { tags: before, photos: beforePhotos });
          setFieldError(`family:${sideKey}`, err.message);
        },
        true,
        true
      );
    },
    [canWriteShared, mergeFamily, send, sendCrop, setFieldError]
  );

  // --- activities and surprises ------------------------------------------

  const addActivity = useCallback(
    (entryId, payload, draftId) => {
      // A surprise is this side's own, so it stays open until they submit.
      // The rest is shared planning and closes when the schedule does.
      const open = (payload || {}).type === SURPRISE ? canWrite() : canWriteShared();
      if (!open) return Promise.resolve(false);
      setFieldError(`activity:${entryId}`, '');
      // Settled by the answer rather than by the first attempt: a dropped
      // request is retried, and the caller's draft has to stay up until one
      // of the tries is actually answered, or until they are all spent.
      // Keyed by the draft, so a second note on the same event is a write
      // of its own and does not cancel the first one's retry.
      return new Promise((resolve) => {
        send(
          `activity:new:${draftId || entryId}`,
          () => createActivity({ ...getBriefIds(), entryId, payload }),
          (res) => {
            resolve(true);
            const entry = entryById(eventsRef.current, entryId);
            if (!entry) return;
            if (res && res.entry) replaceEntries(res.entry);
            else if (res && res.activity) {
              replaceEntries({ ...entry, activities: [...(entry.activities || []), res.activity] });
            }
          },
          (err) => {
            setFieldError(`activity:${entryId}`, err.message);
            resolve(false);
          },
          true,
          false,
          () => resolve(false)
        );
      });
    },
    [canWrite, canWriteShared, replaceEntries, send, setFieldError]
  );

  /**
   * Only the author may edit an activity. The controls are hidden on the
   * partner's rows, so `not_your_activity` is a safety net rather than a path
   * the UI walks into.
   */
  const editActivity = useCallback(
    (entryId, activityId, payload) => {
      const entry = entryById(eventsRef.current, entryId);
      if (!entry) return;
      const before = entry.activities || [];
      const row = before.find((a) => String(a.id) === String(activityId)) || {};
      const open = row.type === SURPRISE ? canWrite() : canWriteShared();
      if (!open) return;
      // Picking a different event moves the row rather than rewriting it,
      // so both events have to be rewritten here: the server answers with a
      // revision only, and the rows are read off the events they sit on.
      const moved = payload.entry_id === undefined
        ? null
        : entryById(eventsRef.current, payload.entry_id);
      const moving = !!moved && String(moved.id) !== String(entry.id);
      const edited = { ...row, ...payload };
      const next = before
        .map((a) => (String(a.id) === String(activityId) ? edited : a))
        .filter((a) => a.is_active !== false);
      const theirs = moving ? (moved.activities || []) : [];
      if (moving) {
        replaceEntries([
          { ...entry, activities: before.filter(
            (a) => String(a.id) !== String(activityId)) },
          { ...moved, activities: [...theirs, edited] },
        ]);
      } else {
        replaceEntries({ ...entry, activities: next });
      }
      owe(`activity:${activityId}`, payload);
      send(
        `activity:${activityId}`,
        (body) => patchActivity({ ...getBriefIds(), activityId, payload: body }),
        (res) => {
          if (res && res.entry) replaceEntries(res.entry);
        },
        (err) => {
          replaceEntries(moving
            ? [{ ...entry, activities: before },
               { ...moved, activities: theirs }]
            : { ...entry, activities: before });
          setFieldError(`activity:${entryId}`, err.message);
        },
        true,
        true
      );
    },
    [canWrite, canWriteShared, replaceEntries, send, setFieldError]
  );

  const removeActivity = useCallback(
    (entryId, activityId) => editActivity(entryId, activityId, { is_active: false }),
    [editActivity]
  );

  // --- navigation --------------------------------------------------------

  const goTo = useCallback(
    (key) => {
      if (!key) return;
      flushText();
      setWdSection(key);
      const scroller = document.querySelector('.wd-content');
      if (scroller) scroller.scrollTop = 0;
    },
    [flushText, setWdSection]
  );

  // A step inside the timeline is a new page as much as a section is, so it
  // opens at the top rather than where the button that led to it was.
  const setPhase = useCallback(
    (next) => {
      setWdPhase(next);
      const scroller = document.querySelector('.wd-content');
      if (scroller) scroller.scrollTop = 0;
    },
    [setWdPhase]
  );

  // --- submit ------------------------------------------------------------

  // Per side. Locks only once the server has accepted: locking optimistically
  // would leave a rejected form read-only with no way back.
  const submit = useCallback(
    (acknowledged) => {
      // Every write still owed lands first. Submitting locks the form, so
      // anything that arrived after it, the last few words typed into a
      // note included, would be refused.
      // Held writes are sent first: a held one goes out under a key of its
      // own, so it is only in flight, and waited on, once it has left.
      return Promise.all(Object.keys(pendingText).map(flushHeld))
        .then(() => Promise.all(Object.keys(inFlight).map(settled)))
        // A write that ran out of retries is sent once more here, so trying
        // submit again is what saves it once the connection is back.
        .then(() => Promise.all(Object.keys(failedWrites).map(
          (key) => failedWrites[key]())))
        .then(() => {
          // One still waiting on a retry, or one that failed again just
          // now, has not saved, and would be shut out by the lock this
          // submit puts on.
          const unsaved = Object.keys(retryTimers).concat(
            Object.keys(keyStates).filter((k) => keyStates[k] === 'failed'));
          if (unsaved.length) {
            const which = Array.from(new Set(unsaved.map(writeLabel)));
            const err = new Error(`These have not saved yet: ${which.join(', ')}. Check your connection and try again.`);
            err.rejected = true;
            err.code = 'not_saved';
            throw err;
          }
          // A write like any other, so a refresh already on its way, which
          // the server answered from before the lock, is dropped rather
          // than opening the form again once the submit has landed.
          writeStamp.count += 1;
          return submitWeddingDay({ ...getBriefIds(), acknowledged });
        })
        .then((res) => {
          // Null is a read only or link-less view, where nothing went out.
          if (!res) return res;
          lockLocal({ acknowledged: true });
          setWdSharedLocked(true);
          return res;
        })
        .catch((err) => {
          // Already submitted elsewhere: that is a lock, not a failure.
          if (err && err.code === REJECT.SUBMITTED) {
            lockLocal();
            return null;
          }
          throw err;
        });
    },
    [lockLocal, setWdSharedLocked]
  );

  return useMemo(
    () => ({
      doc,
      sections,
      section,
      index,
      prevKey,
      nextKey,
      events,
      venues,
      windows,
      dayOpens,
      packageSpan,
      limits,
      family,
      fieldErrors,
      changeLog,
      phase: wdPhase || timelineStopKeys({
        wantsInterview: wdWantsInterview, askInterview: wdAskInterview,
      })[0],
      activeDate,
      datePage: wdDatePage || 0,
      bookingName: wdBookingName || '',
      loading: wdLoading,
      saveState: wdSaveState,
      locked,
      sharedLocked,
      submitted,
      readOnly: isReadOnly(),
      // Why it is read-only, never whether: the backend is the gate.
      canEdit: wdCanEdit,
      denied: wdDenied,
      deniedCode: wdDeniedCode,
      deniedMessage: wdDeniedMessage,
      invalidLink: wdInvalidLink,
      loadFailed: wdLoadFailed,
      role: wdRole,
      toast: wdToast || '',
      dismissToast,
      needsInterview: !!wdNeedsInterview,
      interviews,
      ritualOptions,
      contacts,
      setContact,
      setInterview,
      refresh,
      loadChangeLog,
      setPhase,
      setActiveDate: setWdActiveDate,
      setDatePage: setWdDatePage,
      goTo,
      setText,
      setValue,
      flushText,
      reorder,
      addRitual,
      renameEvent,
      setEventSide,
      setCoupleShot,
      setCoupleShotTime,
      setWantsInterview,
      locksAt: wdLocksAt,
      closed: wdClosed,
      wantsInterview: wdWantsInterview,
      askInterview: wdAskInterview,
      interviewQuestion: wdInterviewQuestion,
      setEventConfirmed,
      removeRitual,
      setTiming,
      uploadPhoto,
      removePhoto,
      setFace,
      addActivity,
      editActivity,
      removeActivity,
      submit,
      applyPayload,
      setWdLoading,
      setWdSection,
      setWdDenied,
      setWdDeniedCode,
      setWdDeniedMessage, setWdInvalidLink, setWdLoadFailed,
    }),
    [doc, sections, section, index, prevKey, nextKey, events, venues, windows, dayOpens, packageSpan, limits, family,
      fieldErrors, changeLog, wdPhase, activeDate, wdDatePage, wdBookingName,
      wdLoading, wdSaveState, locked, sharedLocked, submitted, wdCanEdit, wdDenied,
      wdDeniedCode, wdDeniedMessage, wdRole,
      wdToast, dismissToast, wdNeedsInterview, interviews, ritualOptions, setInterview,
      contacts, setContact,
      refresh,
      loadChangeLog,
      setPhase, setWdActiveDate, setWdDatePage, goTo, setText, setValue,
      flushText, reorder, addRitual, renameEvent, setEventSide, setCoupleShot, setCoupleShotTime, setEventConfirmed,
      setWantsInterview, wdLocksAt, wdClosed, wdWantsInterview, wdAskInterview,
      wdInterviewQuestion, removeRitual,
      setTiming, uploadPhoto, removePhoto,
      setFace,
      addActivity, editActivity, removeActivity, submit, applyPayload, setWdLoading, setWdSection,
      setWdDenied, setWdDeniedCode, setWdDeniedMessage, wdInvalidLink, setWdInvalidLink,
      wdLoadFailed, setWdLoadFailed]
  );
};

/** Loads the form into the store. Call once, from the route component. */
export const useBootstrapWeddingDay = ({ bookingId, briefUuid, readOnly, asRole }) => {
  const {
    applyPayload, refresh, loadChangeLog, flushText, section, loading, invalidLink,
    setWdLoading, setWdDenied, setWdDeniedCode, setWdDeniedMessage, setWdInvalidLink,
    setWdLoadFailed,
  } = useWeddingDay({ bookingId, briefUuid, readOnly, asRole });

  useEffect(() => {
    let alive = true;
    // No brief, or one cut short or edited by hand: a broken link, said
    // without asking the server.
    if (!BRIEF_PATTERN.test(String(briefUuid || '').replace(/-/g, ''))) {
      setWdInvalidLink(true);
      setWdLoading(false);
      return undefined;
    }
    setWdInvalidLink(false);
    setWdLoadFailed(false);
    setWdLoading(true);
    setWdDenied(false);
    bootstrap({ bookingId, briefUuid })
      .then((res) => {
        if (!alive) return;
        // The brief is not this visitor's to read. Nothing else is set: the
        // payload carries no sections to render. The server answers
        // invalid_brief both for a brief that does not exist and for one this
        // user may not see, so a well formed link gets the access card.
        if (res.denied) {
          setWdDenied(true);
          setWdDeniedCode(res.code || '');
          setWdDeniedMessage(res.message || '');
          setWdLoading(false);
          return;
        }
        applyPayload(res);
        loadChangeLog();
        setWdLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        // A brief the server cannot find answers 404, or 400 when it will not
        // parse. A lapsed sign in goes to the login page, which brings them
        // back here. Anything else, a server error or no connection at all,
        // says so with a way to try again, rather than an empty form.
        const status = err && err.response && err.response.status;
        if (status === 400 || status === 404) setWdInvalidLink(true);
        else if (status === 401 && goToLogin()) return;
        else setWdLoadFailed(true);
        setWdLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, briefUuid]);

  // A keystroke in flight must not be lost to a closing tab or a device switch.
  useEffect(() => {
    const onHide = () => flushText();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      onHide();
    };
  }, [flushText]);

  // The partner edits the same document from their own device. Coming back to
  // the tab is the cheapest moment to find out what they changed.
  useEffect(() => {
    if (loading || invalidLink) return undefined;
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      flushText();
      refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [flushText, loading, invalidLink, refresh]);

  // While the timeline is open both sides are usually working in it, so it is
  // the one section worth polling rather than waiting for a focus change.
  const timelineOpen = !!section && (section.kind || section.key) === KIND.TIMELINE;
  useEffect(() => {
    if (!timelineOpen || loading || invalidLink) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      refresh();
    }, SHARED_POLL_MS);
    return () => clearInterval(timer);
  }, [loading, invalidLink, refresh, timelineOpen]);
};
